"use client";

import { MapPin } from "lucide-react";
import { useTranslations } from "next-intl";
import { useMemo, useState, useSyncExternalStore } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from "react-simple-maps";
import countriesTopo from "world-atlas/countries-110m.json";

import {
  APEX_CONTINENTS,
  APEX_DISPLAY_ORDER,
  CONTINENT_BY_ISO_NUM,
  CONTINENT_BY_NAME_FALLBACK,
  KOREA_PLACE_KEYS,
  PLACE_COORDINATES,
  PLACE_MAPS_URL,
  type ContinentKey,
  type PlaceKey,
} from "@/constants/locations";
import { cn } from "@/lib/utils";

const TAB_KEYS = ["all", "korea", "asia", "northAmerica", "europe"] as const;
type TabKey = (typeof TAB_KEYS)[number];

// 대륙 선택 탭 숨김 (피드백: 대륙 선택 숨기기). 되돌리려면 true 로 전환.
const SHOW_CONTINENT_TABS = false;

// www.json 에 대응 키 없음 — 임시 하드코딩
const TAB_LABELS: Record<TabKey, string> = {
  all: "@전체",
  korea: "@한국",
  asia: "@아시아",
  northAmerica: "@북미",
  europe: "@유럽",
};

// www.json 의 location_* 키: 지점명(venue) + 국가/도시명(city) 조합
const PLACE_WWW_KEYS = {
  chelseaPiers: {
    city: "location_usa_newyork",
    venue: "location_usa_newyork_chelsea_piers_golf_club",
  },
  clermontNational: {
    city: "location_usa_orlando",
    venue: "location_usa_orlando_clermont_national",
  },
  greenwichPeninsula: {
    city: "location_uk_london",
    venue: "location_uk_london_greenwich_peninsula_golf_range",
  },
  wexhamPark: {
    city: "location_uk_slough",
    venue: "location_uk_slough_wexham_park_golf_centre",
  },
  siheungSaltbay: {
    city: "location_korea_siheung",
    venue: "location_korea_siheung_saltbay_driving_range",
  },
  ulsanGalaxia: {
    city: "location_korea_ulsan",
    venue: "location_korea_ulsan_galaxia_golf_club",
  },
  incheonGolf24: {
    city: "location_korea_incheon",
    venue: "location_korea_incheon_golf24_hyosung",
  },
  daejeonHanmireu: {
    city: "location_korea_daejeon",
    venue: "location_korea_daejeon_hanmir_daedeok_country_club",
  },
  seoulChoiResearch: {
    city: "location_korea_seoul",
    venue: "location_korea_seoul_choi_wanuk_golf_institute",
  },
  icheonMidasLake: {
    city: "location_korea_icheon",
    venue: "location_korea_icheon_midas_lake_golf_resort",
  },
  banpoRangeX: {
    city: "location_korea_seoul",
    venue: "location_korea_seoul_banpo_range_x",
  },
  saitamaFlags: {
    city: "location_japan_saitama",
    venue: "location_japan_saitama_flags_golf_school",
  },
  zhongliWaitou1: {
    city: "location_taiwan_zhongli",
    venue: "location_taiwan_zhongli_way_to_golf_1",
  },
  newTaipeiWaitou2: {
    city: "location_taiwan_newtaipei",
    venue: "location_taiwan_new_taipei_way_to_golf_2",
  },
} as const satisfies Record<PlaceKey, { city: string; venue: string }>;

const COUNTRY_FILL = "#e5e7eb";
const COUNTRY_FILL_HOVER = "#059669";
const COUNTRY_STROKE = "#ffffff";
const MARKER_FILL = "#059669"; // APEX Green — 설치 지점 핀
const MARKER_FILL_HOVER = "#1b61c9"; // GOLFFIX Blue — 카드 hover 시 강조

// ── 지도 고정 뷰 조정 ────────────────────────────────────────────────
// 아래 4개 값만 바꿔서 지도 위치/확대/크롭을 튜닝한다(인터랙티브 컨트롤 없음).
//
//   MAP_CENTER[0] (경도) ↑ → 뷰가 동쪽(오른쪽 지역)으로 이동
//   MAP_CENTER[1] (위도) ↑ → 뷰가 북쪽(위쪽 지역)으로 이동
//   MAP_SCALE            ↑ → 확대(zoom in) / ↓ → 축소(zoom out)
//   MAP_HEIGHT           ↓ → 위·아래 바다 크롭(가로 폭은 MAP_WIDTH 고정)
//
// viewBox 는 MAP_WIDTH×MAP_HEIGHT. 컨테이너 aspectRatio 도 이 값에 맞춘다.
const MAP_WIDTH = 980;
const MAP_HEIGHT = 410;
const MAP_CENTER: [number, number] = [20, 10]; // [경도, 위도] — 마커 대략 중심
const MAP_SCALE = 150; // geoMercator scale (기본 150)

// 전부 고정값이라 모듈 레벨 상수로 둔다(리렌더마다 재생성 불필요).
const PROJECTION_CONFIG = { center: MAP_CENTER, scale: MAP_SCALE };

const subscribeMounted = () => () => {};
const getMountedClient = () => true;
const getMountedServer = () => false;

// 뷰포트가 md(≥768px) 이상인지 — HTML 의 isDesktop 대응(설치 장소 자동 전체 노출 여부).
// useSyncExternalStore 로 SSR(false)→클라이언트 실제값 전환(하이드레이션 미스매치 방지).
const subscribeDesktop = (cb: () => void) => {
  if (typeof window === "undefined") return () => {};
  const mql = window.matchMedia("(min-width: 768px)");
  mql.addEventListener("change", cb);
  return () => mql.removeEventListener("change", cb);
};
const getDesktopClient = () => window.matchMedia("(min-width: 768px)").matches;
const getDesktopServer = () => false;

function placesForTab(tab: TabKey): readonly PlaceKey[] {
  if (tab === "all") return APEX_DISPLAY_ORDER;
  if (tab === "korea") return KOREA_PLACE_KEYS;
  if (tab === "asia") {
    const koreaSet = new Set<PlaceKey>(KOREA_PLACE_KEYS);
    return APEX_CONTINENTS.asia.places.filter(p => !koreaSet.has(p));
  }
  return APEX_CONTINENTS[tab].places;
}

function PlaceCard({
  placeKey,
  onHoverChange,
}: {
  placeKey: PlaceKey;
  onHoverChange: (placeKey: PlaceKey | null) => void;
}) {
  const t = useTranslations("www");
  const wwwKeys = PLACE_WWW_KEYS[placeKey];
  const placeName = t(wwwKeys.venue);
  // 공유 링크가 있는 지점만 지도 아이콘 표시 (미제공 지점은 아이콘 숨김)
  const mapsUrl = PLACE_MAPS_URL[placeKey];

  return (
    <div
      onMouseEnter={() => onHoverChange(placeKey)}
      onMouseLeave={() => onHoverChange(null)}
      className="flex items-center justify-between gap-1 rounded-2xl border border-gray-700 p-4 transition-colors hover:border-gray-500"
    >
      <div className="min-w-0">
        <p className="text-body-2 text-gray-50">{placeName}</p>
        {/* Figma 1468:4617: 부제 = 국가·도시(city 키가 "대한민국 서울" 반환).
            지점명은 제목에만(중복 제거). */}
        <p className="text-body-3 mt-1 text-gray-400">{t(wwwKeys.city)}</p>
      </div>
      {mapsUrl && (
        <a
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${placeName} - Google Maps`}
          className="inline-flex size-8 shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-800 hover:text-gray-100"
        >
          <MapPin className="size-5" />
        </a>
      )}
    </div>
  );
}

export function ApexLocationsSection() {
  const t = useTranslations("www");
  const [activeTab, setActiveTab] = useState<TabKey>("all");
  const [hoveredPlace, setHoveredPlace] = useState<PlaceKey | null>(null);
  const mounted = useSyncExternalStore(
    subscribeMounted,
    getMountedClient,
    getMountedServer
  );
  const isDesktop = useSyncExternalStore(
    subscribeDesktop,
    getDesktopClient,
    getDesktopServer
  );
  const [showAllLoc, setShowAllLoc] = useState(false);

  const visiblePlaces = useMemo(() => placesForTab(activeTab), [activeTab]);

  // HTML gf-loc: md 미만은 6개만 노출 + "전체 보기" 토글, md(≥768) 이상은 자동 전체 노출.
  const showAll = showAllLoc || isDesktop;
  const displayedPlaces = showAll ? visiblePlaces : visiblePlaces.slice(0, 6);

  // hover 된 마커를 배열 끝으로 옮겨 SVG paint order 상 최상단에 그려지게 함
  const orderedPlaces = useMemo(() => {
    if (!hoveredPlace || !visiblePlaces.includes(hoveredPlace)) {
      return visiblePlaces;
    }
    return [
      ...visiblePlaces.filter(p => p !== hoveredPlace),
      hoveredPlace,
    ] as const;
  }, [visiblePlaces, hoveredPlace]);

  return (
    <section className="bg-gray-900 py-[3.75rem]">
      {/* 앵커 대상을 패딩 안쪽 콘텐츠에 두어, /apex#locations 점프 시 상단 패딩을
          건너뛰고 콘텐츠가 헤더(h-16) 바로 아래 오도록 함. */}
      <div id="locations" className="mx-auto max-w-7xl scroll-mt-24 px-8">
        {/* "@골프픽스 에이펙스 설치 장소 안내": www.json 에 대응 키 없음 — 임시 하드코딩 (Figma 1468:4557) */}
        <h2 className="text-display-2 py-[3.75rem] text-center text-gray-50">
          {t("apex_installed_locations")}
        </h2>

        {SHOW_CONTINENT_TABS && (
          <div className="mt-8 flex flex-wrap gap-1 border-b border-border/50">
            {TAB_KEYS.map(tab => {
              const isActive = activeTab === tab;
              return (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  className={cn(
                    "px-4 py-3 text-sm font-medium transition-colors",
                    isActive
                      ? "border-b-2 border-foreground text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {TAB_LABELS[tab]}
                </button>
              );
            })}
          </div>
        )}

        <div className="mt-6 space-y-8">
          <div
            className="relative overflow-hidden rounded-[2.25rem] bg-white"
            style={{ aspectRatio: `${MAP_WIDTH} / ${MAP_HEIGHT}` }}
          >
            {mounted && (
              <ComposableMap
                projection="geoMercator"
                projectionConfig={PROJECTION_CONFIG}
                width={MAP_WIDTH}
                height={MAP_HEIGHT}
                style={{ width: "100%", height: "auto" }}
              >
                <Geographies geography={countriesTopo as never}>
                  {({ geographies }) =>
                    geographies
                      .map(geo => {
                        const id = geo.id as string | undefined;
                        const name = geo.properties?.name as string | undefined;
                        const continent: ContinentKey | undefined =
                          (id && CONTINENT_BY_ISO_NUM[id]) ||
                          (name && CONTINENT_BY_NAME_FALLBACK[name]) ||
                          undefined;
                        return { geo, continent };
                      })
                      .filter(({ continent }) => continent !== "antarctica")
                      .map(({ geo }) => (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          style={{
                            default: {
                              fill: COUNTRY_FILL,
                              stroke: COUNTRY_STROKE,
                              strokeWidth: 0.5,
                              outline: "none",
                            },
                            hover: {
                              fill: COUNTRY_FILL_HOVER,
                              fillOpacity: 0.45,
                              stroke: COUNTRY_STROKE,
                              strokeWidth: 0.5,
                              outline: "none",
                              cursor: "pointer",
                            },
                            pressed: {
                              fill: COUNTRY_FILL_HOVER,
                              fillOpacity: 0.6,
                              outline: "none",
                            },
                          }}
                        />
                      ))
                  }
                </Geographies>

                {/* APEX 설치 지점 핀. PLACE_COORDINATES 는 [위도, 경도] 저장이므로
                    <Marker> 가 받는 [경도, 위도] 순서로 뒤집어 전달.
                    orderedPlaces 로 hover 마커를 마지막에 그려 최상단 배치. */}
                {orderedPlaces.map(placeKey => {
                  const [lat, lng] = PLACE_COORDINATES[placeKey];
                  const isHovered = placeKey === hoveredPlace;
                  return (
                    <Marker key={placeKey} coordinates={[lng, lat]}>
                      <circle
                        r={isHovered ? 11 : 8}
                        fill={isHovered ? MARKER_FILL_HOVER : MARKER_FILL}
                        fillOpacity={0.9}
                        stroke="#ffffff"
                        strokeWidth={isHovered ? 2 : 1.5}
                        style={{ transition: "r 120ms ease, fill 120ms ease" }}
                      />
                    </Marker>
                  );
                })}
              </ComposableMap>
            )}
          </div>

          <div
            className="grid grid-cols-1 gap-2.5 px-4 min-[600px]:grid-cols-2 lg:grid-cols-3"
            aria-live="polite"
          >
            {displayedPlaces.map(placeKey => (
              <PlaceCard
                key={placeKey}
                placeKey={placeKey}
                onHoverChange={setHoveredPlace}
              />
            ))}
          </div>

          {/* 모바일 전용 "전체 설치 장소 보기 / 접기" 토글 (md 이상은 자동 전체 노출로 숨김).
              i18n 키 없음 — `@` 접두 임시 텍스트. */}
          <div className="text-center md:hidden">
            <button
              type="button"
              onClick={() => setShowAllLoc(v => !v)}
              className="rounded-3xl border border-white/15 px-7 py-3 text-sm font-semibold text-gray-300 transition-colors hover:border-[#6f88ec] hover:text-white"
            >
              {showAll
                ? t("collapse")
                : `${t("view_all_locations")} (${visiblePlaces.length})`}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
